import * as React from "react";
import { withDefault } from "collection-utils";
import { withRouter, RouteComponentProps } from "react-router-dom";

import {
    ModalWrapper,
    Footer,
    Content,
    MainButton,
    IconWrapper,
    LegalText,
    GDPRConsentWrapper,
    GlideLogoWrapper,
} from "./sign-in-up-modal-style";
import {
    signInFromMagicLink,
    signUpWithGoogle,
    signUpEmail,
    onAuthStateChanged,
    removeOnAuthStateChanged,
    AuthenticationFailure,
    isAuthenticatedUser,
    getGoogleUserForSignIn,
    isGoogleUserError,
    isGoogleUserAlreadySignedIn,
    isEmailSignedUp,
    signInFromGoogleUser,
    AuthenticatedUser,
} from "../../../../lib/firebase-auth";
import { showFailureModal } from "../../../ui/smart/modals/modals";
import { assertNever, checkString, dontAwait, isValidEmailAddress } from "common/Support";
import { googleConnectionFailureMessage } from "../../../../lib/google-api";
import { GoogleLogo } from "webapp/lib/google-logo";
import queryString from "querystring";
import { signInToFirebaseWithCustomToken } from "webapp/lib/auth";
import { logError } from "common/debug-print";
import { removeQueryStrings } from "lib/browser-hacks";
import AppIcon from "common/app-icon";
import { withFeatureSetting } from "hooks/use-feature-setting";
import { localStorageGetItem, localStorageSetItem } from "webapp/lib/local-storage";
import { withModalProvider } from "designer/modals-v2/modal-provider-v2/modal-provider-v2";
import { RenderModalAction, SimpleModalContextProps } from "designer/modals-v2/modal-provider-v2/modal-context";
import WindowedModalV2 from "designer/modals-v2/windowed-modal/windowed-modal";
import ActionModal from "../../../modals/smart/action-modal/action-modal";
import { builderDocURLWithCampaign } from "analytics";
import { ThirdPartyCookiesModal } from "designer/modals/views/third-party-cookies-modal.tsx/third-party-cookies-modal";

// The following key and two functions let you check if some user ever authed in the current browser.
// This is useful cause we want to default to the signIn form instead of signUp, in that case.
const USER_ONCE_AUTHED_KEY = "browser_once_authed";

function setBrowserOnceAuthed(): void {
    // This is just a flag, but we need to set _something_ so we'll just set the string 'true'
    localStorageSetItem(USER_ONCE_AUTHED_KEY, "true");
}

function getBrowserOnceAuthed(): boolean {
    const maybeStoredValue = localStorageGetItem(USER_ONCE_AUTHED_KEY);
    return maybeStoredValue !== undefined;
}

const glideLogoColor = (
    <svg width="150" height="95" viewBox="0 0 150 95" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M24.3828 80.3606C31.4928 79.051 35.9729 75.0758 37.8114 69.1372L38.5289 76.5649L44.6999 75.4282L44.5878 48.7094L23.8615 52.5272L23.8653 59.4564L36.7438 57.0841L36.7446 58.6239C36.7593 63.3111 34.5804 71.2023 24.9215 72.9815C18.0799 74.2418 13.0716 71.4544 11.1999 61.0894C9.38932 51.0632 11.23 37.8444 23.1694 35.6451C29.2062 34.5331 33.5677 36.8096 35.431 42.9062L43.8817 39.8096C41.0654 30.7386 33.2573 26.647 22.4582 28.6362C3.61 32.1081 -0.332367 49.424 2.08985 62.8375C4.76897 77.6737 14.3215 82.214 24.3828 80.3606Z"
            fill="currentColor"
        />
        <path
            d="M58.7877 22.0842L50.7387 23.5668L50.9552 74.2759L59.0713 72.7809L58.7877 22.0842Z"
            fill="currentColor"
        />
        <path
            d="M66.0963 30.6777L74.8161 29.0715L74.7626 20.3315L66.1099 21.9254L66.0963 30.6777ZM66.655 71.3839L74.704 69.9013L74.6241 34.9167L66.5751 36.3994L66.655 71.3839Z"
            fill="currentColor"
        />
        <path
            d="M94.1869 67.1524C98.8151 66.2999 102.466 63.1074 103.934 58.5672L103.967 64.5109L111.614 63.1023L111.33 12.4056L103.281 13.8883L103.366 35.0822C100.778 30.7291 96.2281 30.0972 92.2035 30.8385C80.4654 33.0007 78.0818 45.8995 79.7333 55.0451C81.6784 65.8166 88.6867 68.1656 94.1869 67.1524ZM96.4152 60.3021C93.4639 60.8457 89.4752 60.2505 88.1418 52.8663C87.1509 47.3789 87.5605 38.1336 94.8718 36.7869C99.0975 36.0085 103.648 38.1802 103.622 46.8648L103.636 49.2422C103.632 54.2128 101.781 59.3136 96.4152 60.3021Z"
            fill="currentColor"
        />
        <path
            d="M124.386 45.35L147.996 41.0008C148.355 38.7648 148.207 36.412 147.84 34.3797C146.128 24.8953 138.857 22.2447 131.143 23.6656C118.198 26.0502 114.99 37.8408 116.691 47.2574C118.856 59.2483 127.895 60.9433 134.938 59.6459C142.182 58.3115 146.412 54.1024 147.921 47.8746L141.425 46.0612C140.379 50.2439 138.282 52.4501 134.257 53.1914C129.629 54.0439 125.567 52.2723 124.398 45.4177L124.386 45.35ZM131.842 29.0669C135.665 28.3626 139.16 29.6788 140.028 34.4887C140.2 35.4371 140.225 36.3425 140.188 36.9092L123.822 39.924C124.008 34.4298 126.543 30.043 131.842 29.0669Z"
            fill="currentColor"
        />
    </svg>
);

export type Mode = "signIn" | "signUp" | "signingIn" | "sendingEmail" | "emailSent";

interface Props extends RouteComponentProps {
    // If you don't specify a startingMode, we check if this browser ever authed (see getBrowserOnceAuthed)
    // In that case we start in signIn mode, else in signUp.
    startingMode?: Mode;
    onFinish: (canceled: boolean) => void;
    showModePivotFooter?: boolean;
    emailLinkAuth: boolean;
    modals: SimpleModalContextProps;
}

interface State {
    mode: Mode;
    allowEmail: boolean;
    emailAddress: string;
}

class SignInUpModalWithDispatch extends React.PureComponent<Props, State> {
    readonly state: State;
    constructor(props: Props) {
        super(props);

        const defaultStartingMode = getBrowserOnceAuthed() ? "signIn" : "signUp";
        const startingMode = props.startingMode ?? defaultStartingMode;

        this.state = {
            mode: startingMode,
            allowEmail: false,
            emailAddress: "",
        };
    }

    private checkRef = React.createRef<HTMLInputElement>();
    private inputRef = React.createRef<HTMLInputElement>();

    private switchMode = () => {
        const newMode: Mode = this.state.mode === "signIn" ? "signUp" : "signIn";
        this.setState({ mode: newMode });
    };

    private handleAuthError = async (result: AuthenticationFailure, failMode: Mode): Promise<void> => {
        switch (result.kind) {
            case "declined-sign-in": {
                this.props.onFinish(true);
                this.props.modals.DEPRECATED_removeAllModals();
                break;
            }

            case "unexpected-error": {
                await showFailureModal(result.message ?? "Something went wrong while signing in.");
                break;
            }

            case "popup-blocked": {
                showSignUpPopupBlockedFlow(this.props.modals.showModal, this.props.onFinish);
                break;
            }

            case "no-google-libraries": {
                await showFailureModal(googleConnectionFailureMessage);
                break;
            }

            case "cookies-blocked": {
                await this.props.modals.showModal<void>(resolve => {
                    return <ThirdPartyCookiesModal resolve={resolve} />;
                });
                break;
            }

            case "link-expired": {
                await showFailureModal("Magic link has expired.");
                break;
            }

            default: {
                return assertNever(result.kind);
            }
        }

        this.setState({ mode: failMode });
        return;
    };

    private handleAuthenticatedUserOrError = async (
        authResult: AuthenticatedUser | AuthenticationFailure,
        failMode: Mode
    ): Promise<void> => {
        if (!isAuthenticatedUser(authResult)) {
            return this.handleAuthError(authResult, failMode);
        } else {
            setBrowserOnceAuthed();
            this.props.onFinish(false);
            this.props.modals.DEPRECATED_removeAllModals();
            return;
        }
    };

    private signInWithGoogle = async (): Promise<void> => {
        this.setState({ mode: "signingIn" });
        const googleUserResponse = await getGoogleUserForSignIn();

        if (isGoogleUserError(googleUserResponse)) {
            return this.handleAuthError(googleUserResponse, "signIn");
        }

        if (isGoogleUserAlreadySignedIn(googleUserResponse)) {
            this.props.onFinish(false);
            this.props.modals.DEPRECATED_removeAllModals();
            return;
        }

        const googleUser = googleUserResponse.user;
        const email = googleUser.getBasicProfile().getEmail();

        const didSignUp = await isEmailSignedUp(email);

        if (!didSignUp) {
            const shouldCreateAccount = await this.showNotSignedUpModal();

            if (shouldCreateAccount) {
                const authResult = await signUpWithGoogle(undefined, true);
                return this.handleAuthenticatedUserOrError(authResult, "signIn");
            }

            this.setState({ mode: "signIn" });
            return;
        }

        const authResult = await signInFromGoogleUser(googleUser);
        return this.handleAuthenticatedUserOrError(authResult, "signIn");
    };

    private signInWithEmail = async (): Promise<void> => {
        const { emailAddress } = this.state;
        this.setState({ mode: "signingIn" });

        const didSignUp = await isEmailSignedUp(emailAddress);

        if (!didSignUp) {
            const shouldCreateAccount = await this.showNotSignedUpModal();

            if (!shouldCreateAccount) {
                this.setState({ mode: "signIn" });
                return;
            }
        }

        return this.signUpOrInWithEmail("signIn");
    };

    private showNotSignedUpModal = () => {
        return this.props.modals.showModal<boolean>(resolve => {
            return (
                <WindowedModalV2 canClose={false} onClose={() => resolve(false)}>
                    <ActionModal
                        header="This account does not exist"
                        description="Do you want to create a new one?"
                        acceptText="Create account"
                        destroyText="Sign in with a different email"
                        acceptAction={() => resolve(true)}
                        destroyAction={() => resolve(false)}
                    />
                </WindowedModalV2>
            );
        });
    };

    private signInFromMagicLink = async (magicLink: string): Promise<void> => {
        this.setState({ mode: "signingIn" });

        const authResult = await signInFromMagicLink(magicLink);
        return this.handleAuthenticatedUserOrError(authResult, "signIn");
    };

    private signUpWithGoogle = async (): Promise<void> => {
        this.setState({ mode: "signingIn" });

        const authResult = await signUpWithGoogle(this.state.allowEmail);
        return this.handleAuthenticatedUserOrError(authResult, "signUp");
    };

    private signUpOrInWithEmail = async (mode: "signUp" | "signIn") => {
        const { emailAddress } = this.state;
        if (!isValidEmailAddress(emailAddress)) {
            await this.props.modals.showSimpleInfoModal(
                "Invalid Email Address",
                "The email address you entered is not a valid format. Please enter a valid email address"
            );
            return;
        }
        this.setState({ mode: "sendingEmail" });

        // FIXME: signUpEmail is both for signUp and signIns.
        // We should be more clear here.
        const authResult = await signUpEmail(emailAddress, mode);
        if (authResult === true) {
            // The email was sent, nothing to do...
            this.setState({ mode: "emailSent" });
            return;
        }

        return this.handleAuthenticatedUserOrError(authResult, "signUp");
    };

    public componentDidMount() {
        const queryStringParsed = queryString.parse(window.location.search.substr(1));

        const { firebaseAuthToken, apiKey, email } = queryStringParsed;

        if (apiKey !== undefined) {
            // "apiKey" in the query string means we've been redirected here from Firebase, and we can authenticate a
            // user account using the given key
            const magicLink = checkString(window.location.href);
            const cb = () => {
                removeOnAuthStateChanged(cb);
                this.signInFromMagicLink(magicLink)
                    .catch(e => {
                        logError(`Could not sign in to Firebase with magic link: ${e}`);
                    })
                    .finally(() => {
                        removeQueryStrings("apiKey", "email");
                    });
            };
            onAuthStateChanged(cb);
        } else if (typeof email === "string") {
            // TODO: We should validate that this email makes sense before trying to send the magic link to it
            this.setState({ emailAddress: email }, () => {
                dontAwait(this.signUpOrInWithEmail("signUp"));
            });
        } else if (firebaseAuthToken !== undefined) {
            // This path generally should not be used for authentication.
            // We only added this to get past Google Auth in end to end testing,
            // since Google Auth doesn't like to be invoked from scripted browsers.
            // NOTE: We have two location where we look for query strings, and then
            // remove all of them.  The other one's in `GlideDesigner/index.tsx`.
            const firebaseAuthTokenString = checkString(firebaseAuthToken);
            signInToFirebaseWithCustomToken(firebaseAuthTokenString).catch(e => {
                logError(`Could not sign in to Firebase with custom token: ${e}`);
            });
        }

        removeQueryStrings("stripeConnectFailed", "firebaseAuthToken", "email");
    }

    public componentWillUnmount() {
        if (this.state.mode !== "signingIn") {
            this.props.onFinish(true);
        }
    }

    public componentDidUpdate(_prevProps: Props, prevState: State) {
        if (
            (this.state.mode === "signIn" && prevState.mode !== "signIn") ||
            (this.state.mode === "signUp" && prevState.mode !== "signUp")
        ) {
            this.inputRef.current?.focus();
        }
    }

    public render() {
        const mode = this.state.mode;
        const { showModePivotFooter, emailLinkAuth } = this.props;
        const showFooter = withDefault(showModePivotFooter, true);
        let footerText: string;
        let content: React.ReactNode;

        if (mode === "signIn") {
            footerText = "Create a new account";
            content = (
                <>
                    <MainButton className="secondary" onClick={this.signInWithGoogle}>
                        <IconWrapper>
                            <GoogleLogo />
                        </IconWrapper>
                        Sign in with Google
                    </MainButton>
                    {emailLinkAuth && (
                        <>
                            <div className="or-bar">
                                <div className="or-bar-bar" />
                                <div>Or</div>
                                <div className="or-bar-bar" />
                            </div>
                            <div className="email-region">
                                <input
                                    autoFocus
                                    className="form-input"
                                    placeholder="you@example.com"
                                    ref={this.inputRef}
                                    type="email"
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            void this.signUpOrInWithEmail("signIn");
                                        }
                                    }}
                                    onChange={e => this.setState({ emailAddress: e.target.value })}
                                />
                                <MainButton className="email-button" onClick={this.signInWithEmail}>
                                    <IconWrapper>
                                        <AppIcon className="inline-icon" icon="envelope" />
                                    </IconWrapper>
                                    <span>Sign in with Email</span>
                                </MainButton>
                            </div>
                        </>
                    )}
                </>
            );
        } else if (mode === "signUp") {
            footerText = "I already have an account";
            content = (
                <>
                    <MainButton className="secondary" onClick={this.signUpWithGoogle}>
                        <IconWrapper>
                            <GoogleLogo />
                        </IconWrapper>
                        Sign up with Google
                    </MainButton>
                    {emailLinkAuth && (
                        <>
                            <div className="or-bar">
                                <div className="or-bar-bar" />
                                <div>Or</div>
                                <div className="or-bar-bar" />
                            </div>
                            <div className="email-region">
                                <input
                                    autoFocus
                                    className="form-input"
                                    placeholder="Enter your work email"
                                    ref={this.inputRef}
                                    type="email"
                                    onKeyDown={e => {
                                        if (e.key === "Enter") {
                                            void this.signUpOrInWithEmail("signUp");
                                        }
                                    }}
                                    onChange={e => this.setState({ emailAddress: e.target.value })}
                                />
                                <MainButton className="email-button" onClick={() => this.signUpOrInWithEmail("signUp")}>
                                    <IconWrapper>
                                        <AppIcon className="inline-icon" icon="envelope" />
                                    </IconWrapper>
                                    <span>Sign up with Email</span>
                                </MainButton>
                            </div>
                        </>
                    )}
                    <GDPRConsentWrapper onClick={() => this.setState({ allowEmail: !this.state.allowEmail })}>
                        <input
                            ref={this.checkRef}
                            type="checkbox"
                            checked={this.state.allowEmail}
                            onChange={e => this.setState({ allowEmail: e.target.checked })}
                        />{" "}
                        Email me about new features
                    </GDPRConsentWrapper>
                </>
            );
        } else if (mode === "sendingEmail") {
            content = "Sending email…";
            footerText = "";
        } else if (mode === "emailSent") {
            content = (
                <div>
                    We sent <span className="link">{this.state.emailAddress}</span> a link to sign in. Please check your
                    email.
                </div>
            );
            footerText = "Try again";
        } else {
            content = "Signing in…";
            footerText = "";
        }

        // eslint-disable-next-line
        const footerLink = <a onClick={this.switchMode}>{footerText}</a>;

        return (
            <ModalWrapper>
                <Content>
                    <GlideLogoWrapper>{glideLogoColor}</GlideLogoWrapper>
                    {content}
                </Content>

                {["signIn", "signUp"].includes(mode) && (
                    <LegalText>
                        By signing {mode === "signUp" ? "up" : "in"}, you agree to our{" "}
                        <a href="https://www.glideapps.com/legal#terms" target="_blank" rel="noopener noreferrer">
                            Terms
                        </a>{" "}
                        &amp;{" "}
                        <a href="https://www.glideapps.com/privacy" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                        </a>
                        .
                    </LegalText>
                )}

                {showFooter && footerText !== "" && <Footer>{footerLink}</Footer>}
            </ModalWrapper>
        );
    }
}

export const SignInUpModal = withFeatureSetting(withModalProvider(withRouter(SignInUpModalWithDispatch)), [
    "emailLinkAuth",
]);

// FIXME: Is this the appropriate place for this documentation?
const blockedPopupsURL = builderDocURLWithCampaign("https://docs.glideapps.com/all/etc/faq/sign-in-issues");

// We have a problem with our sign-in flow: there's no way to tell
// whether you actually signed up until it's too late.
//
// This would generally be fine, but the vanilla pop-up blockers of
// Chrome and Firefox have decided that one button click = one JavaScript
// triggered pop-up.
//
// If someone tries to sign in before they've signed up, we try to
// pop up a second pop-up to authorize against their drive. This
// second pop-up is blocked, and users then complain that they can't
// sign in, even though they never even signed up!
//
// We're just assuming that the majority cause of these complaints
// is due to going through the sign in flow instead of the sign up
// flow. Signing up again is always a no-op so this is only has
// cosmetic implications if they've already signed up.
export function showSignUpPopupBlockedFlow(
    showModal: (randerModal: RenderModalAction<void>) => Promise<void>,
    onFinish: (canceled: boolean) => void
): void {
    const showSignInUpModal = () => {
        dontAwait(
            showModal(resolve => {
                const finishAndCloseModal = (canceled: boolean) => {
                    onFinish(canceled);
                    resolve();
                };

                return (
                    <WindowedModalV2 canClose={false} onClose={resolve} hideClosingButton={true}>
                        <SignInUpModal onFinish={finishAndCloseModal} showModePivotFooter={false} />
                    </WindowedModalV2>
                );
            })
        );
    };

    dontAwait(
        showModal(resolve => {
            return (
                <WindowedModalV2 canClose={false} onClose={resolve} hideClosingButton={true}>
                    <ActionModal
                        header="Could not sign in to Google"
                        description={
                            <span>
                                You need to enable pop-ups for <b>{window.location.host}</b> to sign in with Google.
                            </span>
                        }
                        acceptText="Try again"
                        acceptAction={showSignInUpModal}
                        acceptActionClosesModal={false}
                        informationText="Learn more"
                        informationAction={() => {
                            window.open(blockedPopupsURL, "_blank");
                        }}
                        informationActionClosesModal={false}
                    />
                </WindowedModalV2>
            );
        })
    );
}
